import React, {useCallback} from 'react'
import type {IBreadcrumbProps} from 'pepsico-ds'
import {Breadcrumb} from 'pepsico-ds'
import {useNavigate} from 'react-router-dom'

type BreadcrumbWrapperProps = IBreadcrumbProps

/**
 * BreadcrumbWrapper - A wrapper component for the pepsico-ds Breadcrumb component.
 * Provides consistent styling and behavior across the application with React Router navigation.
 *
 * @param {BreadcrumbWrapperProps} props - Component props
 * @param {Array} props.breadcrumbs - Optional; The list to populate the Breadcrumbs
 * @param {number} props.maxBreadcrumbCount - Optional; Maximum number of breadcrumbs before collapsing
 * @returns {React.ReactElement} Rendered breadcrumb component
 */
const BreadcrumbWrapper: React.FC<BreadcrumbWrapperProps> = React.memo(
  ({className = '', breadcrumbs, maxBreadcrumbCount, ...rest}) => {
    const navigate = useNavigate()

    // Memoize the navigation function to prevent unnecessary re-renders
    const handleNavigation = useCallback(
      (href: string) => {
        navigate(href)
      },
      [navigate],
    )

    // Transform breadcrumbs to include onClick handlers for React Router navigation
    const transformedBreadcrumbs = breadcrumbs?.map(breadcrumb => {
      // If an onClick is already provided, preserve it and strip href
      if (breadcrumb.onClick) {
        return {
          ...breadcrumb,
          href: undefined,
        }
      }

      // If href is '#' or empty, don't make it clickable
      if (!breadcrumb.href || breadcrumb.href === '#') {
        return {
          ...breadcrumb,
          href: undefined,
          onClick: undefined,
        }
      }

      // Store href for navigation
      const hrefToNavigate = breadcrumb.href

      // Remove href so the design-system component does not trigger native navigation.
      return {
        ...breadcrumb,
        href: undefined,
        onClick: () => {
          handleNavigation(hrefToNavigate)
        },
      }
    })

    return (
      <Breadcrumb
        className={className}
        breadcrumbs={transformedBreadcrumbs}
        maxBreadcrumbCount={maxBreadcrumbCount}
        {...rest}
      />
    )
  },
)

BreadcrumbWrapper.displayName = 'BreadcrumbWrapper'

export default BreadcrumbWrapper
